import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { authApi, legalDocumentsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';
import type {
    ActiveRoleAssignmentResponse,
    GetLegalDocumentResponse,
    LegalDocumentMutableStatus,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';

export function LegalDocumentDetailPage() {
    const log = createComponentLogger('LegalDocumentDetailPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const [mutationError, setMutationError] = useState<string | null>(null);
    const params = useParams();
    const legalDocumentUuidParam: string | undefined = params.id;
    const legalDocumentUuid: string = useMemo(() => {
        if (legalDocumentUuidParam === undefined) {
            throw new Error('Missing legal document UUID');
        }
        const parseResult = z.string().uuid().safeParse(legalDocumentUuidParam);
        if (!parseResult.success) {
            throw new Error('Invalid legal document UUID');
        }
        return parseResult.data;
    }, [legalDocumentUuidParam]);

    const { data, isLoading, error } = useQuery<GetLegalDocumentResponse>({
        queryKey: ['legalDocument', legalDocumentUuid],
        queryFn: async () => {
            return await legalDocumentsApi.getLegalDocument(legalDocumentUuid);
        },
        staleTime: 0,
    });
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    const canManageLifecycle: boolean = hasInternalAdminPrivileges(activeRoleAssignment ?? null);
    const updateStatusMutation = useMutation({
        mutationFn: async (status: LegalDocumentMutableStatus) => {
            return await legalDocumentsApi.updateLegalDocumentStatus(legalDocumentUuid, { status });
        },
        onSuccess: async () => {
            setMutationError(null);
            await queryClient.invalidateQueries({ queryKey: ['legalDocument', legalDocumentUuid] });
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
            await queryClient.invalidateQueries({ queryKey: ['pendingRequiredLegalDocuments'] });
        },
        onError: (mutationErr: unknown) => {
            log.error('Failed to update legal document status', mutationErr);
            setMutationError('Unable to update legal document status.');
        },
    });

    if (isLoading) {
        return (
            <div className="p-6">
                <p>Loading...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6">
                <p>Error loading legal document.</p>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="p-6">
                <p>Missing legal document data.</p>
            </div>
        );
    }

    const doc = data.legal_document;
    const srcDoc: string = buildLegalDocumentIframeSrcDoc({
        legalDocumentUuid: doc.legal_document_uuid,
        legalDocumentContentType: doc.legal_document_content_type,
        legalDocumentText: doc.legal_document_text,
    });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {mutationError === null ? null : (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {mutationError}
                </div>
            )}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold">{doc.legal_document_title}</h1>
                        <StatusBadge status={doc.status} />
                    </div>
                    <p className="text-sm text-gray-600">
                        Version: {doc.legal_document_version}
                    </p>
                    <p className="text-sm text-gray-600">
                        In force: {new Date(doc.in_force_start).toLocaleString()} -{' '}
                        {doc.in_force_end === null
                            ? 'No end date'
                            : new Date(doc.in_force_end).toLocaleString()}
                    </p>
                </div>
                <div className="flex gap-2">
                    {canManageLifecycle && doc.status === Status.ACTIVE ? (
                        <Button
                            variant="outline"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                updateStatusMutation.mutate(Status.DISABLED);
                            }}
                        >
                            Disable
                        </Button>
                    ) : null}
                    {canManageLifecycle &&
                    (doc.status === Status.PENDING || doc.status === Status.DISABLED) ? (
                        <Button
                            variant="outline"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => {
                                setMutationError(null);
                                updateStatusMutation.mutate(Status.ACTIVE);
                            }}
                        >
                            {doc.status === Status.PENDING ? 'Activate' : 'Re-enable'}
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        onClick={() => {
                            runInBackground(
                                Promise.resolve(
                                    breadcrumbNavigate({
                                        source: 'panel',
                                        target: {
                                            url: '/legal-documents',
                                            title: 'Legal Documents',
                                        },
                                    })
                                ),
                                (err: unknown) => {
                                    log.error('Navigation failed', err);
                                }
                            );
                        }}
                    >
                        Back
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Document</CardTitle>
                </CardHeader>
                <CardContent>
                    <iframe
                        title="Legal Document"
                        sandbox="allow-scripts"
                        className="w-full h-[70vh] border rounded"
                        srcDoc={srcDoc}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
